import * as stylex from '@stylexjs/stylex'
import { type ForwardedRef, memo, useRef, type MouseEvent, type SVGProps, useImperativeHandle, useCallback } from 'react'
import isEqual from 'react-fast-compare'
import { CircleShape } from './CircleShape'
import { PawShape } from './PawShape'

const getScaleRatio = (event: MouseEvent<HTMLElement>) => {
  const rect = event.currentTarget.getBoundingClientRect()
  const x = event.clientX
  const y = event.clientY
  const deltaX = Math.max(Math.abs(rect.left - x), rect.right - x)
  const deltaY = Math.max(Math.abs(rect.top - y), rect.bottom - y)
  const farthestCornerOffset = Math.sqrt(Math.pow(deltaX, 2) + Math.pow(deltaY, 2))
  const minDimension = Math.min(event.currentTarget.offsetWidth, event.currentTarget.offsetHeight)

  return farthestCornerOffset / (minDimension / 2)
}

const getPosition = ({
  event,
  rect,
  center,
}: {
  event: MouseEvent<HTMLElement>
  rect: DOMRect
  center: boolean
}): {
  left: string
  top: string
} => {
  if (center) {
    return {
      left: '50%',
      top: '50%',
    }
  }
  return {
    left: `${event.clientX - rect.x}px`,
    top: `${event.clientY - rect.y}px`,
  }
}

const styles = stylex.create({
  root: {
    borderRadius: 'inherit',
    overflow: 'hidden',
    pointerEvents: 'none',
    position: 'absolute',
    zIndex: 1,
    height: '100%',
    left: 0,
    top: 0,
    width: '100%',
  },
  rippleShape: {
    fill: 'currentColor',
    opacity: 0,
    position: 'absolute',
    transformOrigin: '50% 50%',
    height: '100%',
    left: 0,
    top: 0,
    width: '100%',
  },
})

/** 漣漪形狀 SVG 元件的 props */
export interface ShapeProps extends SVGProps<SVGSVGElement> {
  /** SVG 元素的 ref */
  rootRef?: ForwardedRef<SVGSVGElement>
}

/** Ripple 的命令式操作介面 */
export interface RippleHandle {
  /** 從滑鼠事件位置啟動漣漪動畫 */
  startRipple: (event: MouseEvent<HTMLElement>) => void
  /** 停止所有正在進行的漣漪動畫 */
  stopRipple: () => void
}

interface RippleStack {
  animation: Animation
  animationStatus: 'playing' | 'finished'
  node: SVGSVGElement
}

export interface RippleProps {
  /** 命令式操作的 ref（startRipple / stopRipple） */
  rootRef?: ForwardedRef<RippleHandle>
  /**
   * 漣漪擴散動畫持續時間（毫秒）
   * @default 600
   */
  duration?: number
  /**
   * 漣漪形狀
   * @default 'circle'
   */
  shape?: 'circle' | 'paw'
}

const OPACITY = 0.2

const RippleComponent = (props: RippleProps) => {
  const {
    rootRef: rootRefProp,
    duration = 600,
    shape = 'circle',
  } = props
  const rippleRef = useRef<HTMLDivElement>(null)
  const rippleShapeRef = useRef<SVGSVGElement>(null)
  const rippleStack = useRef<RippleStack[]>([])

  const startRipple = useCallback((event: MouseEvent<HTMLElement>) => {
    if (!rippleRef.current || !rippleShapeRef.current) return

    const scaleRatio = getScaleRatio(event)
    const clone = rippleShapeRef.current.cloneNode(true) as SVGSVGElement
    rippleRef.current.appendChild(clone)

    const rippleRect = rippleRef.current.getBoundingClientRect()
    /**
     * If element ratio is 1:1 position should be center.
     */
    const center = rippleRect.width === rippleRect.height

    const cloneRect = clone.getBoundingClientRect()
    const { left, top } = getPosition({
      event,
      rect: cloneRect,
      center,
    })
    clone.style.left = left
    clone.style.top = top

    const animation = clone.animate([
      {
        transform: 'translate(-50%, -50%) scale(0)',
        opacity: OPACITY,
      },
      {
        transform: `translate(-50%, -50%) scale(${scaleRatio})`,
        opacity: OPACITY,
      },
    ], {
      duration,
      easing: 'ease-out',
      fill: 'forwards',
    })
    const length = rippleStack.current.push({
      animation,
      animationStatus: 'playing',
      node: clone,
    })
    const index = length - 1
    animation.onfinish = () => {
      if (!rippleStack.current[index]) return
      rippleStack.current[index].animationStatus = 'finished'
    }
  }, [duration])

  const stopRipple = useCallback(() => {
    rippleStack.current.forEach((stack) => {
      const fadeOut = () => {
        const fadeOutAnimate = stack.node.animate([
          { opacity: OPACITY }, { opacity: 0 },
        ], {
          duration: 200,
          easing: 'ease-out',
          fill: 'forwards',
        })
        fadeOutAnimate.onfinish = () => {
          stack.node.remove()
        }
      }
      if (stack.animationStatus === 'finished') {
        fadeOut()
      }
      else {
        stack.animation.onfinish = () => {
          fadeOut()
        }
      }
    })
    rippleStack.current = []
  }, [])

  useImperativeHandle(rootRefProp, () => ({
    startRipple,
    stopRipple,
  }), [
    startRipple,
    stopRipple,
  ])

  const ShapeComponent = shape === 'paw' ? PawShape : CircleShape

  return (
    <div ref={rippleRef} {...stylex.props(styles.root)}>
      <ShapeComponent rootRef={rippleShapeRef} {...stylex.props(styles.rippleShape)} />
    </div>
  )
}

/**
 * Ripple 是點擊漣漪動畫效果元件，通常搭配 ButtonBase 使用。
 * 支援圓形與貓掌兩種形狀，透過 ref 命令式觸發動畫。
 */
export const Ripple = memo(RippleComponent, isEqual)
